// src/managerPortal/pages/MenuManagementPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // For redirecting on 403
import axios from "axios";
import "../styles/EventManagement.css";
import SidebarMenu from "../components/SidebarMenu";

// Common buttons
import AddButton from "../components/common/AddButton";
import EditButton from "../components/common/EditButton";
import DeleteButton from "../components/common/DeleteButton";

// Modals
import MenuModal from "../components/modals/MenuModal";
import DeleteConfirmModal from "../components/modals/MenuDeleteConfirmModal";

// Import our dynamic API URL
import apiUrl from "../../config";

// Import the Pagination component
import Pagination from "../components/Pagination";

function MenuManagementPage() {
    const navigate = useNavigate();

    const [menuItems, setMenuItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    // Category filter
    const [selectedCategory, setSelectedCategory] = useState("All");

    // Subcategory filter
    const [selectedSubcategory, setSelectedSubcategory] = useState("");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // Use state for dynamic items-per-page

    // Modals
    const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
    const [selectedItem, setSelectedItem] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTargetItem, setDeleteTargetItem] = useState(null);

    // Fetch menu items on mount
    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/manager/menu`, {
                    withCredentials: true,
                });
                setMenuItems(response.data);
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    navigate("/manager/no-access");
                } else {
                    console.error("Error fetching menu items:", error);
                }
            }
        };
        fetchMenuItems();
    }, [navigate]);

    // ─────────────────────────────────────────────────────────────────────────────
    // Open/Close Modals
    // ─────────────────────────────────────────────────────────────────────────────
    const openAddModal = () => {
        setModalMode("add");
        setSelectedItem(null);
        setIsMenuModalOpen(true);
    };

    const openEditModal = (itemData) => {
        setModalMode("edit");
        setSelectedItem(itemData);
        setIsMenuModalOpen(true);
    };

    const openDeleteModal = (itemData) => {
        setDeleteTargetItem(itemData);
        setIsDeleteModalOpen(true);
    };

    const closeMenuModal = () => {
        setIsMenuModalOpen(false);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Add/Edit/Delete handlers
    // ─────────────────────────────────────────────────────────────────────────────
    const handleAddItem = async (formData) => {
        try {
            const response = await axios.post(
                `${apiUrl}/api/manager/menu`,
                formData,
                {
                    withCredentials: true,
                }
            );
            const { newItem } = response.data;
            setMenuItems((prev) => [...prev, newItem]);
            closeMenuModal();
        } catch (error) {
            console.error("Error adding menu item:", error);
        }
    };

    const handleEditItem = async (id, formData) => {
        try {
            const response = await axios.put(
                `${apiUrl}/api/manager/menu/${id}`,
                formData,
                {
                    withCredentials: true,
                }
            );
            const { updatedItem } = response.data;
            setMenuItems((prev) =>
                prev.map((item) => (item._id === id ? updatedItem : item))
            );
            closeMenuModal();
        } catch (error) {
            console.error("Error editing menu item:", error);
        }
    };

    const handleDeleteItem = async () => {
        if (!deleteTargetItem) return;
        try {
            await axios.delete(
                `${apiUrl}/api/manager/menu/${deleteTargetItem._id}`,
                { withCredentials: true }
            );
            setMenuItems((prev) =>
                prev.filter((item) => item._id !== deleteTargetItem._id)
            );
            closeDeleteModal();
        } catch (error) {
            console.error("Error deleting menu item:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Sorting
    // ─────────────────────────────────────────────────────────────────────────────
    const handleSort = (field) => {
        let order = "asc";
        if (sortField === field && sortOrder === "asc") {
            order = "desc";
        }
        setSortField(field);
        setSortOrder(order);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Filtering by Search
    // ─────────────────────────────────────────────────────────────────────────────
    const filteredBySearch = menuItems.filter((item) => {
        const lowerQuery = searchQuery.toLowerCase();
        const itemName = item.name ? item.name.toLowerCase() : "";
        const itemDesc = item.description ? item.description.toLowerCase() : "";
        return itemName.includes(lowerQuery) || itemDesc.includes(lowerQuery);
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // Filter by Category
    // ─────────────────────────────────────────────────────────────────────────────
    const filteredByCategory =
        selectedCategory === "All"
            ? filteredBySearch
            : filteredBySearch.filter((item) => item.category === selectedCategory);

    // ─────────────────────────────────────────────────────────────────────────────
    // Filter by Subcategory
    // ─────────────────────────────────────────────────────────────────────────────
    const filteredByCatAndSubcat = selectedSubcategory
        ? filteredByCategory.filter((item) => item.subcategory === selectedSubcategory)
        : filteredByCategory;

    // ─────────────────────────────────────────────────────────────────────────────
    // Sorting the final filtered items
    // ─────────────────────────────────────────────────────────────────────────────
    const sortedItems = sortField
        ? [...filteredByCatAndSubcat].sort((a, b) => {
            if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
            if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
            return 0;
        })
        : filteredByCatAndSubcat;

    // ─────────────────────────────────────────────────────────────────────────────
    // Pagination Calculations
    // ─────────────────────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

    // Clear search text
    const clearSearch = () => {
        setSearchQuery("");
        setCurrentPage(1);
    };

    // Category filter options
    const categories = [
        "All",
        "Appetizer",
        "Main Food",
        "Sandwiches",
        "Drinks",
        "Bottles",
        "Shisha",
    ];

    // Derive unique subcategories based on selected category
    let uniqueSubcategories = [];
    if (selectedCategory !== "All") {
        uniqueSubcategories = [
            ...new Set(
                menuItems
                    .filter((item) => item.category === selectedCategory)
                    .map((item) => item.subcategory)
                    .filter(Boolean)
            ),
        ];
    }

    return (
        <div className="layout">
            <SidebarMenu />
            <div className="main-content">
                <div className="card">
                    <div className="card-header">
                        <h4>Menu Items</h4>
                    </div>
                    <div className="card-body">
                        {/* Search + Add Button Container */}
                        <div className="search-add-container">
                            <div className="search-wrapper">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by name or description..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {/* Clear button for the search */}
                                <button className="btn-clear" onClick={clearSearch}>
                                    Clear
                                </button>
                            </div>
                            <AddButton onClick={openAddModal} />
                        </div>

                        {/* Category Filter Buttons */}
                        <div style={{ marginBottom: "1rem" }}>
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    style={{
                                        marginRight: "0.5rem",
                                        padding: "6px 12px",
                                        borderRadius: "4px",
                                        border:
                                            cat === selectedCategory
                                                ? "2px solid #333"
                                                : "1px solid #ccc",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setSelectedCategory(cat);
                                        setSelectedSubcategory(""); // reset subcategory
                                        setCurrentPage(1);
                                    }}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Subcategory Filter Buttons (only if we have subcategories) */}
                        {uniqueSubcategories.length > 0 && (
                            <div style={{ marginBottom: "1rem" }}>
                                {/* 'All Subcategories' button */}
                                <button
                                    style={{
                                        marginRight: "0.5rem",
                                        padding: "6px 12px",
                                        borderRadius: "4px",
                                        border:
                                            selectedSubcategory === ""
                                                ? "2px solid #333"
                                                : "1px solid #ccc",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setSelectedSubcategory("");
                                        setCurrentPage(1);
                                    }}
                                >
                                    All Subcategories
                                </button>

                                {uniqueSubcategories.map((subcat) => (
                                    <button
                                        key={subcat}
                                        style={{
                                            marginRight: "0.5rem",
                                            padding: "6px 12px",
                                            borderRadius: "4px",
                                            border:
                                                subcat === selectedSubcategory
                                                    ? "2px solid #333"
                                                    : "1px solid #ccc",
                                            backgroundColor: "#fff",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            setSelectedSubcategory(subcat);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {subcat}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="table-responsive">
                            <table className="events-table">
                                <thead>
                                <tr>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort("name")}
                                    >
                                        Name{" "}
                                        {sortField === "name"
                                            ? sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                    <th>Description</th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort("category")}
                                    >
                                        Category{" "}
                                        {sortField === "category"
                                            ? sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                    <th>Subcategory</th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort("price")}
                                    >
                                        Price{" "}
                                        {sortField === "price"
                                            ? sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                    <th className="actions-col">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentItems.map((item) => (
                                    <tr key={item._id}>
                                        <td>{item.name}</td>
                                        <td>{item.description}</td>
                                        <td>{item.category}</td>
                                        <td>{item.subcategory || ""}</td>
                                        <td>${item.price}</td>
                                        <td className="actions-col">
                                            <div className="action-buttons">
                                                <EditButton onClick={() => openEditModal(item)} />
                                                <DeleteButton onClick={() => openDeleteModal(item)} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Use the Pagination component */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(value) => {
                                setItemsPerPage(value);
                                setCurrentPage(1);
                            }}
                            storageKey="menuItemsPerPage" // This key uniquely identifies the setting for this page
                        />
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isMenuModalOpen && (
                <MenuModal
                    mode={modalMode}
                    itemData={selectedItem}
                    onClose={closeMenuModal}
                    onAdd={handleAddItem}
                    onEdit={handleEditItem}
                />
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <DeleteConfirmModal
                    itemTitle={deleteTargetItem?.name}
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteItem}
                />
            )}
        </div>
    );
}

export default MenuManagementPage;
